import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Shared ⓘ tooltip. Rendered in a portal with position:fixed so card clip-paths and overflow never cut it.
// Opens above the icon, flips below when there is no room under the sticky nav, and stays inside the viewport.
// Hover opens it for a mouse; a tap toggles it on touch screens (tap elsewhere or Esc closes).
// Pass rows={[[label, value], ...]} for one fact per line, or children for a short sentence.
const GAP = 8; const EDGE = 8;
function navBottom() {
  const n = document.querySelector('nav, .nav');
  if (!n) return 0;
  const r = n.getBoundingClientRect();
  return getComputedStyle(n).position === 'sticky' || getComputedStyle(n).position === 'fixed' ? Math.max(0, r.bottom) : 0;
}

export default function Info({ children, rows, label = 'How this works', testid }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btn = useRef(null); const tip = useRef(null);

  const place = useCallback(() => {
    if (!btn.current || !tip.current) return;
    const b = btn.current.getBoundingClientRect(); const t = tip.current.getBoundingClientRect();
    const vw = document.documentElement.clientWidth; const vh = window.innerHeight;
    const top0 = navBottom() + EDGE;
    let below = b.top - GAP - t.height < top0; // no room above (nav included)
    if (below && b.bottom + GAP + t.height > vh && b.top - top0 > vh - b.bottom) below = false; // more room above after all
    const top = below ? b.bottom + GAP : b.top - GAP - t.height;
    let left = b.left + b.width / 2 - t.width / 2;
    left = Math.max(EDGE, Math.min(left, vw - EDGE - t.width));
    const arrow = Math.max(10, Math.min(t.width - 10, b.left + b.width / 2 - left));
    setPos({ top, left, below, arrow });
  }, []);

  useLayoutEffect(() => { if (open) place(); else setPos(null); }, [open, place, children, rows]);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (btn.current && !btn.current.parentNode.contains(e.target) && !(tip.current && tip.current.contains(e.target))) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('scroll', place, true); window.addEventListener('resize', place);
    document.addEventListener('pointerdown', close); document.addEventListener('keydown', esc);
    return () => { window.removeEventListener('scroll', place, true); window.removeEventListener('resize', place); document.removeEventListener('pointerdown', close); document.removeEventListener('keydown', esc); };
  }, [open, place]);

  const body = rows && rows.length
    ? <dl className="af-tip-rows">{rows.map(([k, v]) => <React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>)}</dl>
    : children;
  return (
    <span className="af-info" onPointerEnter={(e) => { if (e.pointerType === 'mouse') setOpen(true); }} onPointerLeave={(e) => { if (e.pointerType === 'mouse') setOpen(false); }}>
      <button type="button" ref={btn} className="af-info-btn" aria-label={label} aria-expanded={open} data-testid={testid} onClick={() => setOpen((o) => !o)}>i</button>
      {open && createPortal(
        <span ref={tip} className={`af-tip ${pos && pos.below ? 'af-tip-below' : 'af-tip-above'}`} role="tooltip"
          style={pos ? { top: pos.top, left: pos.left, '--arrow': `${pos.arrow}px` } : { top: 0, left: 0, visibility: 'hidden' }}>
          {rows && rows.length && label ? <span className="af-tip-h">{label}</span> : null}{body}
        </span>, document.body)}
    </span>
  );
}
